'use client';

import BackButton from '@/components/layout/BackButton';
import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { useCart } from '@/features/checkout/CartProvider';
import CheckoutSteps from '@/features/checkout/components/CheckoutSteps';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link.js';
import { useMemo, useState } from 'react';
import { Button, Container, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../forms/_formPatterns.scss';
import '../_checkoutScreen.shared.scss';
import '../checkout.scss';
import CartEmptyState from './CartEmptyState';
import CartHeading from './CartHeading';
import './cartItem.scss';
import CartLineItem from './CartLineItem';
import { readCartMessage } from './cartMessages';
import './cartPage.scss';
import { CartLineItemSkeleton, CartSummarySkeleton } from './CartSkeletons';
import CartSubtotalOffcanvas from './CartSubtotalOffcanvas';
import CartSummaryContent from './CartSummaryContent';
import { filterVisibleCartItems, hasFreeShippingBenefit } from './cartUtils';
import './subtotalCard.scss';
import { useIsCompactCartLayout } from './useIsCompactCartLayout';

export default function CartPage({ currentUser, messages = {} }) {
  const {
    addItem,
    appliedBenefits,
    cartItems,
    discount,
    hasHydrated,
    itemsPrice,
    shippingPrice,
    totalPrice,
    totalItems,
    updateQty,
    removeItem,
  } = useCart();
  const isCompactCartLayout = useIsCompactCartLayout();
  const prefersReducedMotion = useReducedMotion();
  const [removedPlaceholders, setRemovedPlaceholders] = useState([]);
  const visibleCartItems = useMemo(
    () => filterVisibleCartItems(cartItems, appliedBenefits),
    [appliedBenefits, cartItems],
  );
  const currency = cartItems[0]?.currency || 'USD';
  const discountAmount = Number(discount?.amount || 0);
  const hasDiscountAmount = discountAmount > 0;
  const hasFreeShipping = hasFreeShippingBenefit(appliedBenefits);

  const continueHref = currentUser
    ? '/shipping'
    : buildAuthHref('/login', '/shipping');
  const itemVariants = prefersReducedMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 12, scale: 0.985 },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: 0.28, ease: [0.22, 0.86, 0.36, 1] },
        },
        exit: {
          opacity: 0,
          y: -8,
          scale: 0.985,
          transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
        },
      };

  const handleRemoveItem = (removedItem) => {
    if (!removedItem?.cartLineId && !removedItem?.productNumber) {
      return;
    }

    const lineId = removedItem.cartLineId || removedItem.productNumber;

    removeItem(lineId);
    setRemovedPlaceholders((current) => [
      ...current.filter((entry) => entry.cartLineId !== lineId),
      { cartLineId: lineId, item: removedItem },
    ]);

    const toastId = toast.info(
      ({ closeToast }) => (
        <div className="d-flex align-items-center justify-content-between gap-3">
          <span>
            {readCartMessage(
              messages,
              'cartScreen',
              'toast.removed',
              '{{name}} removed from cart.',
            ).replace('{{name}}', removedItem.name || '')}
          </span>
          <Button
            type="button"
            variant="link"
            className="custom-btn-link app-button--compact"
            aria-label={readCartMessage(
              messages,
              'cartScreen',
              'toast.undoAria',
              'Undo removing {{name}}',
            ).replace('{{name}}', removedItem.name || '')}
            onClick={() => {
              addItem(removedItem, removedItem.qty);
              setRemovedPlaceholders((current) =>
                current.filter((entry) => entry.cartLineId !== lineId),
              );
              closeToast?.();
            }}
          >
            {readCartMessage(messages, 'cartScreen', 'toast.undo', 'Undo')}
          </Button>
        </div>
      ),
      {
        autoClose: 5000,
        onClose: () =>
          setRemovedPlaceholders((current) =>
            current.filter((entry) => entry.cartLineId !== lineId),
          ),
      },
    );

    setRemovedPlaceholders((current) =>
      current.map((entry) =>
        entry.cartLineId === lineId ? { ...entry, toastId } : entry,
      ),
    );
  };

  if (!hasHydrated) {
    return (
      <section
        className={`cart-screen-wrapper ${isCompactCartLayout ? 'has-subtotal-offcanvas' : ''}`.trim()}
      >
        <div className="checkout-shell">
          <BackButton forceVisible fallback="/shop" label="Back to Shop" />
          <CheckoutSteps
            currentStep={1}
            isAuthenticated={Boolean(currentUser)}
            messages={messages}
          />

          <Container fluid className="cart-screen fade-out-screen">
            <div className="cart-screen__layout">
              <div className="cart-items">
                <CartHeading messages={messages} />
                <ListGroup
                  as="div"
                  className="list-group"
                  aria-label={readCartMessage(
                    messages,
                    'cartScreen',
                    'aria.loadingCartItems',
                    'Loading cart items',
                  )}
                >
                  {Array.from({ length: 3 }).map((_, index) => (
                    <ListGroup.Item key={index} as="div" className="p-0">
                      <CartLineItemSkeleton index={index} />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>

              {!isCompactCartLayout ? (
                <aside className="subtotal-card-wrapper">
                  <div className="checkout-summary checkout-summary--section subtotal-card">
                    <div className="subtotal-body">
                      <CartSummarySkeleton />
                    </div>
                  </div>
                </aside>
              ) : null}
            </div>
          </Container>

          {isCompactCartLayout ? (
            <CartSubtotalOffcanvas
              messages={messages}
              totalItems={0}
              appliedBenefits={[]}
              cartItems={[]}
              continueHref={continueHref}
              currency={currency}
              currentUser={currentUser}
              discountAmount={0}
              hasDiscountAmount={false}
              hasFreeShipping={false}
              itemsPrice={0}
              shippingPrice={0}
              totalPrice={0}
            >
              <CartSummarySkeleton />
            </CartSubtotalOffcanvas>
          ) : null}
        </div>
      </section>
    );
  }

  if (!visibleCartItems.length) {
    return (
      <section className="cart-screen-wrapper">
        <div className="checkout-shell">
          <BackButton forceVisible fallback="/shop" label="Back to Shop" />
          <CheckoutSteps
            currentStep={1}
            isAuthenticated={Boolean(currentUser)}
            messages={messages}
          />
          <Container className="cart-screen fade-out-screen pt-4 px-3 px-md-0">
            <CartEmptyState messages={messages} />
          </Container>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`cart-screen-wrapper ${isCompactCartLayout ? 'has-subtotal-offcanvas' : ''}`.trim()}
    >
      <div className="checkout-shell">
        <BackButton forceVisible fallback="/shop" label="Back to Shop" />
        <CheckoutSteps
          currentStep={1}
          isAuthenticated={Boolean(currentUser)}
          messages={messages}
        />

        <Container fluid className="cart-screen fade-out-screen">
          <div className="cart-screen__layout">
            <div className="cart-items">
              <CartHeading messages={messages} />
              <ListGroup
                as="div"
                className="list-group"
                aria-label={readCartMessage(
                  messages,
                  'cartScreen',
                  'aria.cartItemList',
                  'List of items currently in your cart',
                )}
              >
                <AnimatePresence initial={false}>
                  {visibleCartItems.map((item) => (
                    <ListGroup.Item
                      as={motion.div}
                      layout={!prefersReducedMotion}
                      variants={itemVariants}
                      initial={prefersReducedMotion ? false : 'hidden'}
                      animate={prefersReducedMotion ? false : 'visible'}
                      exit={prefersReducedMotion ? false : 'exit'}
                      key={item.cartLineId || item.productNumber}
                      className="p-0"
                    >
                      <CartLineItem
                        item={{ ...item, appliedBenefits }}
                        messages={messages}
                        onQtyChange={updateQty}
                        onRemove={handleRemoveItem}
                      />
                    </ListGroup.Item>
                  ))}

                  <Link
                    href="/shop"
                    className="app-button app-button--ghost px-2 mt-3"
                  >
                    {readCartMessage(
                      messages,
                      'cart',
                      'subtotalCard.continueShopping',
                      'Continue Shopping',
                    )}
                  </Link>

                  {removedPlaceholders.map(({ cartLineId, item, toastId }) => (
                    <ListGroup.Item
                      as={motion.div}
                      layout={!prefersReducedMotion}
                      initial={
                        prefersReducedMotion ? false : { opacity: 0, y: 10 }
                      }
                      animate={
                        prefersReducedMotion ? false : { opacity: 1, y: 0 }
                      }
                      exit={
                        prefersReducedMotion ? false : { opacity: 0, y: -8 }
                      }
                      key={`removed-${cartLineId}`}
                      className="p-0 removed-list-item"
                    >
                      <div className="removed-chip d-flex align-items-center justify-content-between">
                        <span>
                          {readCartMessage(
                            messages,
                            'cartScreen',
                            'toast.removed',
                            '{{name}} removed from cart.',
                          ).replace('{{name}}', item.name || '')}
                        </span>
                        <Button
                          variant="link"
                          className="custom-btn-link app-button--compact"
                          onClick={() => {
                            addItem(item, item.qty);
                            if (toastId) {
                              toast.dismiss(toastId);
                            }
                            setRemovedPlaceholders((current) =>
                              current.filter(
                                (entry) => entry.cartLineId !== cartLineId,
                              ),
                            );
                          }}
                        >
                          {readCartMessage(
                            messages,
                            'cartScreen',
                            'toast.undo',
                            'Undo',
                          )}
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </AnimatePresence>
              </ListGroup>
            </div>

            {!isCompactCartLayout ? (
              <aside className="subtotal-card-wrapper">
                <div className="checkout-summary checkout-summary--section subtotal-card">
                  <div className="subtotal-body">
                    <CartSummaryContent
                      appliedBenefits={appliedBenefits}
                      cartItems={visibleCartItems}
                      continueHref={continueHref}
                      currency={currency}
                      currentUser={currentUser}
                      discountAmount={discountAmount}
                      hasDiscountAmount={hasDiscountAmount}
                      hasFreeShipping={hasFreeShipping}
                      itemsPrice={itemsPrice}
                      messages={messages}
                      shippingPrice={shippingPrice}
                      totalItems={totalItems}
                      totalPrice={totalPrice}
                    />
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </Container>

        {isCompactCartLayout ? (
          <CartSubtotalOffcanvas
            appliedBenefits={appliedBenefits}
            cartItems={visibleCartItems}
            continueHref={continueHref}
            currency={currency}
            currentUser={currentUser}
            discountAmount={discountAmount}
            hasDiscountAmount={hasDiscountAmount}
            hasFreeShipping={hasFreeShipping}
            itemsPrice={itemsPrice}
            messages={messages}
            shippingPrice={shippingPrice}
            totalItems={totalItems}
            totalPrice={totalPrice}
          />
        ) : null}
      </div>
    </section>
  );
}
